<script  lang="ts">
    import { service } from "@frontend/services";
    import { GetCachedToken, me } from "@frontend/user";
    import { AddGroupRequest, AuthenticatedRequestOfAddGroupRequest, type ApiResultOfGroupDTO } from "@frontend/walletapi";
    import { groups } from "@frontend/grouplist";
    import { translate, currentLanguage } from "../Translator";
    
    export let dismiss: () => void;
     
    let emptyGroup: AddGroupRequest = {
        Name: "",
        ParentId: undefined,
        UserId: undefined
    };
    
    let newGroup: AddGroupRequest = {
        ...emptyGroup,
        UserId: $me?.Id ? String($me.Id) : undefined
    };
    
    let result: ApiResultOfGroupDTO | undefined = undefined;
    
    // Función para filtrar los grupos disponibles
    $: availableGroups = $groups.filter(g => {

        // Si es Owner, puede ver todos los grupos
        if ($me?.Role === 'Owner') {
          
            return true;
        }
        
        // Obtener todos los grupos en la jerarquía del usuario
        const myGroup = $groups.find(group => String(group.Id) === String($me?.GroupId));
  

        // Si soy hijo de alguien, incluir a mi padre y hermanos
        const myParentGroup = myGroup?.ParentId ? $groups.find(g => String(g.Id) === String(myGroup.ParentId)) : null;


        // Obtener todos los grupos relacionados
        const isMyGroup = String(g.Id) === String($me?.GroupId);
        const isMyParent = String(myGroup?.ParentId) === String(g.Id);
        const isMyChild = String(g.ParentId) === String($me?.GroupId);
        const isMySibling = myGroup?.ParentId && String(g.ParentId) === String(myGroup.ParentId);

        return isMyGroup || isMyParent || isMyChild || isMySibling;
    });

    // Función para crear visualización jerárquica de grupos
    function getGroupDisplayName(group: any): string {
        if ($me?.Role !== 'Owner') {
            return `[${group.Id}] ${group.Name}`;
        }
        
        // Para Owners, mostrar jerarquía completa
        let displayName = `[${group.Id}] ${group.Name}`;
        
        // Si tiene padre, mostrar la ruta completa
        if (group.ParentId) {
            const parent = $groups.find(g => g.Id === group.ParentId);
            if (parent) {
                displayName = `[${group.Id}] └─ ${group.Name} `;
            }
        } else {
            displayName = `[${group.Id}] 📁 ${group.Name}`;
        }
        
        return displayName;
    }

    // Ordenar los grupos por jerarquía
    $: sortedAvailableGroups = availableGroups.sort((a, b) => {
        // Si es Owner, ordenar jerárquicamente
        if ($me?.Role === 'Owner') {
            // Primero los grupos raíz (sin padre)
            if (!a.ParentId && b.ParentId) return -1;
            if (a.ParentId && !b.ParentId) return 1;
            
            // Luego por ID para mantener consistencia
            return Number(a.Id) - Number(b.Id);
        }
        
        // Para otros roles, mantener la lógica original
        if (String(a.Id) === String($me?.GroupId)) return -1;
        if (String(b.Id) === String($me?.GroupId)) return 1;
        if (String(a.ParentId) === String($me?.GroupId)) return -1;
        if (String(b.ParentId) === String($me?.GroupId)) return 1;
        return Number(a.Id) - Number(b.Id);
    });

    async function AddGroup() {
        result = await service.walletapi.addGroup({
            Token: GetCachedToken(),
            Data: newGroup,
        });

        if (result.Success) {
            setTimeout(dismiss, 2000);
        }
    }
</script>

<h2 class="ui centered dividing header">{$translate('New Group')}</h2>
{#if result && result.Success && result.Data}
    <div class="ui positive message">
        <div class="header">
            {$translate('Added successfully')}
        </div>
    </div>

    <div class="ui segment" style="display: flex;">
        <table class="receit-table" style="flex-grow: 1;">
            <tr>
                <td class="receit-title">{$translate('Id')}</td>
                <td class="receit-value">#{result.Data.Id}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Group Name')}</td>
                <td class="receit-value">{result.Data.Name}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Parent-id')}</td>
                <td class="receit-value">{result.Data.ParentId ?? "None"}</td>
            </tr>
        </table>
        
    </div>
{:else}
    {#if result && !result.Success}
        <div class="ui negative message">
            <div class="header">Error</div>
            <p>{result.ErrorMessage ?? "Unknown error when adding group"}</p>
        </div>
    {/if}

    <form class="ui form" on:submit|preventDefault={AddGroup}>
        {#if $me?.Role === 'Owner'}
            <div class="ui info message">
                <div class="header">{$translate('Owner Permissions')}</div>
                <p>{$translate('As an Owner, you can create subgroups under any existing group in the system.')}</p>
            </div>
        {/if}
        
        <div class="field">
            <label>{$translate('Group Name')}</label>
            <input type="text" bind:value={newGroup.Name} placeholder="Group Name" required>
        </div>
        
        <div class="field">
            <label>{$translate('Parent-id')}</label>
            <select class="ui dropdown" bind:value={newGroup.ParentId}>
                <option value="">{$translate('Select a parent group...')}</option>
                {#each sortedAvailableGroups as group}
                    <option value={group.Id}>{getGroupDisplayName(group)}</option>
                {/each}
            </select>
        </div>

        <div class="ui buttons">
            <button class="ui button" type="button" on:click={dismiss}>{$translate('Cancel')}</button>
            <div class="or"></div>
            <button class="ui positive button" type="submit">{$translate('Add Group')}</button>
        </div>
    </form>
{/if}

<style>
    .receit-table {
        width: 100%;
        border-collapse: collapse;
    }
    .receit-title {
        font-weight: bold;
        padding: 8px;
        border-bottom: 1px solid #ddd;
    }
    .receit-value {
        padding: 8px;
        border-bottom: 1px solid #ddd;
    }
</style>
